import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

type PageHeaderProps = {
  title: ReactNode;
  /** Small uppercase label above the title naming the area the page sits in. */
  eyebrow?: ReactNode;
  /** Supporting copy under the title. */
  description?: ReactNode;
  /** Link to the parent page, shown above the rest of the header. */
  backLink?: { to: string; label: string };
  className?: string;
};

/** The top of a page: an optional back link and eyebrow, the serif title, and
 * an optional description. Use it for page headers so they read the same
 * across the app. */
export default function PageHeader({
  title,
  eyebrow,
  description,
  backLink,
  className,
}: PageHeaderProps) {
  return (
    <header className={className}>
      {backLink && (
        <Link
          to={backLink.to}
          className="mb-6 flex w-fit items-center gap-2 text-sm font-semibold text-evergreen hover:text-soil"
        >
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
          {backLink.label}
        </Link>
      )}
      {eyebrow && (
        <p className="mb-2 text-xs font-bold uppercase tracking-[.14em] text-evergreen">
          {eyebrow}
        </p>
      )}
      <h1 className="font-serif text-[34px] font-normal leading-[1.15] text-soil">
        {title}
      </h1>
      {description && (
        <div className="mt-3 max-w-2xl text-lg text-oat-700">{description}</div>
      )}
    </header>
  );
}
